import { Component, signal, computed, inject, ChangeDetectionStrategy, DestroyRef, OnInit, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventsService } from 'src/app/module/events/services/events.service';
import { EventData } from '../../models/event.model';
import { catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { decodeBase64OrReturn } from 'src/app/shared/utils/base64.util';
import { LoadingComponent } from 'src/app/shared/components/loading/loading.component';
import { PaginatorModule } from 'primeng/paginator';
import { CardCarouselComponent } from 'src/app/shared/components/card-carousel/card-carousel.component';
import { CardEventComponent } from 'src/app/shared/components/card-event/card-event.component';


/** "Eventos da Semana" só é exibido quando há MAIS do que esta quantidade de eventos. */
const WEEKLY_MIN_EVENTS = 3;

enum MonthFormat {
  JAN = 0,
  FEV = 1,
  MAR = 2,
  ABR = 3,
  MAI = 4,
  JUN = 5,
  JUL = 6,
  AGO = 7,
  SET = 8,
  OUT = 9,
  NOV = 10,
  DEZ = 11,
}

@Component({
    selector: 'app-events-list',
    templateUrl: './events-list.component.html',
    styleUrls: ['./events-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    LoadingComponent,
    PaginatorModule,
    CardCarouselComponent,
    CardEventComponent
]
})
export class EventsListComponent implements OnInit {
  private readonly eventsService = inject(EventsService);
  private readonly destroyRef = inject(DestroyRef);

  // Optional organizer scope (slug or organizer user ExternalId). Null on the home → all events.
  readonly organizer = input<string | null>(null);

  readonly section = signal<string>('');
  readonly currentPage = signal<number>(1);
  readonly pageSize = signal<number>(12);
  readonly first = signal<number>(0);
  readonly loading = signal<boolean>(false);
  
  readonly featuredEvents = signal<(EventData & { src: string; title: string })[]>([]);
  readonly weeklyEvents = signal<(EventData & { src: string; title: string })[]>([]);
  readonly showWeeklyEvents = computed(() => this.weeklyEvents().length > WEEKLY_MIN_EVENTS);
  readonly pagedEvents = signal<(EventData & { src: string; title: string })[]>([]);
  readonly totalEvents = signal<number>(0);

  private readonly isLoadingEvents = signal<boolean>(false);

  readonly MONTH_FORMAT = MonthFormat;


  ngOnInit(): void {
    this.loadInitialData();
  }

  setSection(section: string) {
    this.section.set(section);
  }

  private loadInitialData(): void {
    // Carrega todos os dados em paralelo para melhor performance
    this.loading.set(true);
    
    forkJoin({
      all: this.eventsService.getAllEvents(this.currentPage(), this.pageSize(), 1, true, this.organizer()),
      highlights: this.eventsService.getHighlights(this.organizer()),
      weekly: this.eventsService.getWeekly(this.organizer())
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(() => {
        this.loading.set(false);
        return of({ all: { data: [], total: 0 }, highlights: { data: [] }, weekly: { data: [] } });
      })
    ).subscribe({
      next: (results: any) => {
        // Processa eventos em destaque
        if (results.highlights?.data) {
          this.featuredEvents.set(this.processEventsArray(results.highlights.data));
        }

        // Processa eventos da semana
        if (results.weekly?.data) {
          this.weeklyEvents.set(this.processEventsArray(results.weekly.data));
        }

        // Processa todos os eventos
        if (results.all?.data) {
          const filtered = this.filterFutureEvents(results.all.data);
          const processed = this.processEventsArray(filtered);
          this.pagedEvents.set(processed);
          this.totalEvents.set(results.all.total || processed.length);
        }

        this.loading.set(false);
      }
    });
  }

  formatDate(apiDate: { day: string; month: string; year: string }): {
    day: string;
    month: string;
    year: string;
  } {
    return {
      day: apiDate.day.padStart(2, '0'),
      month: this.MONTH_FORMAT[parseInt(apiDate.month) - 1],
      year: apiDate.year,
    };
  }

  private processEventsArray(events: any[]): (EventData & { src: string; title: string })[] {
    return events.map((event: any) => {
      const id = event.id;
      const slugUrl = event.slugUrl;
      const image = event.image || event.src || '';
      const name = event.name || event.title || '';
      const city = event.city || '';
      const state = event.state || '';
      const local = event.local || '';
      const description = event.description || null;
      
      let day = event.day;
      let month = event.month;
      let year = event.year;
      
      if (!day && event.startDate) {
        const dateMatch = event.startDate.match(/(\d{2})\/(\d{2})\/(\d{4})/);
        if (dateMatch) {
          day = dateMatch[1];
          month = dateMatch[2];
          year = dateMatch[3];
        }
      }
      
      const formattedDate =
        day && month && year
          ? this.formatDate({ day, month, year })
          : { day: '00', month: 'JAN', year: new Date().getFullYear().toString() };
      
      return {
        id: id,
        slugUrl: slugUrl,
        name: name,
        image: image,
        src: image,
        title: name,
        city: city || '',
        state: state || '',
        day: formattedDate.day,
        month: formattedDate.month,
        year: formattedDate.year,
        local: local,
        description: description,
        type: event.type || '',
        startDate: event.startDate || '',
        endDate: event.endDate || '',
        address: event.address || {} as any,
        courtesy: event.courtesy || {} as any,
        idMeansReceipt: event.idMeansReceipt || '',
        idOrganizer: event.idOrganizer || '',
        variants: event.variants || [],
        highlighted: event.highlighted || false,
        status: event.status || 0,
      } as EventData & { src: string; title: string };
    });
  }

  private filterFutureEvents(events: any[]): any[] {
    const today = new Date();
    return events.filter((event: any) => {
      if (!event.endDate) return false;
      try {
        const [datePart, timePart, period] = event.endDate.split(' ');
        const [day, month, year] = datePart.split('/').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        const adjustedHours = period === 'PM' && hours < 12 ? hours + 12 : hours;
        const eventDate = new Date(year, month - 1, day, adjustedHours, minutes);
        return eventDate >= today;
      } catch {
        return false;
      }
    });
  }

  atobDecodific(encodedString: string | null | undefined): string {
    if (!encodedString) return '';
    const sanitizedString = encodedString.replace(/[\r\n]+/g, '').trim();
    return decodeBase64OrReturn(sanitizedString);
  }

  getAllEvents(): void {
    if (this.isLoadingEvents()) {
      return;
    }

    this.isLoadingEvents.set(true);
    this.loading.set(true);
    
    this.eventsService
      .getAllEvents(this.currentPage(), this.pageSize(), 1, true, this.organizer())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.loading.set(false);
          this.isLoadingEvents.set(false);
          return of({ data: [], total: 0 });
        })
      )
      .subscribe({
        next: (res: any) => {
          const filtered = this.filterFutureEvents(res.data || []);
          const processed = this.processEventsArray(filtered);
          this.pagedEvents.set(processed);
          this.totalEvents.set(res.total || processed.length);
          this.loading.set(false);
          this.isLoadingEvents.set(false);
        }
      });
  }

  onPageChange(event: any) {
    this.currentPage.set(event.page + 1);
    this.pageSize.set(event.rows);
    this.first.set(event.first);
    this.isLoadingEvents.set(false);
    this.getAllEvents();
  }

}
